import 'url-search-params-polyfill'
import Cookies from 'js-cookie'
import { stringify } from 'query-string'

import { PRODUCT_TARGET_PAGE_CODE } from 'shared/consts'
import { CSRF_FIELD_NAME } from 'shared/consts/httpHeaders'
import { ECONDA_VISITOR_ID_COOKIE } from 'shared/consts/econdaCookieIds'

import assert from 'shared/utils/assert'
import { createTenantPath } from 'server/utils/routing'

import APIService from './APIService'

class ClientFockService extends APIService {
  constructor(props) {
    super(props)

    const { tenant, language, locale, coreShopEnvironment, mountPoints } = props

    assert(tenant, 'ClientFockService: no "tenant" option provided')
    assert(
      coreShopEnvironment,
      'ClientFockService: no "coreShopEnvironment" option provided'
    )
    assert(language, 'ClientFockService: no "language" option provided')

    this.baseParams = {
      tenant,
      tenantPath: createTenantPath(tenant, coreShopEnvironment),
      language,
      locale,
      coreShopEnvironment,
      mountPoints
    }
  }

  /* eslint-disable-next-line class-methods-use-this */
  getUserSessionDataPath() {
    return 'shopwebclientsession.go'
  }

  getNotepadResourcePath(upid) {
    const { mountPoints } = this.baseParams
    return mountPoints.getNotepadEntry(upid)
  }

  getNotepadResourceDeletePath(upid) {
    const { mountPoints } = this.baseParams
    return mountPoints.getNotepadDeleteEntry(upid)
  }

  static getUpdateDataPath() {
    return 'updateNotepad.go'
  }

  static extractEmvidFromCookie(cookie) {
    if (!cookie) {
      return null
    }
    const parts = cookie.split(':')
    return parts.length > 0 ? parts[0] : null
  }

  async fetchUserSessionData(passedConfig) {
    // we need to set the base to '/'
    // as we need to connect to old shop and not to fock
    const config = { ...passedConfig, baseURL: '/' }
    this.logger.debug('Fetching UserSessionData')
    config.params = { ...config.params, cb: new Date().getTime() }

    const response = await this.get(this.getUserSessionDataPath(), config)
    this.logger.debug('UserSessionData fetched')
    return response
  }

  /* eslint-disable-next-line class-methods-use-this */
  postProductRatingPath() {
    return 'rateProduct.go'
  }

  async postProductRating(formValues) {
    // we need to set the base to '/'
    // as we need to connect to old shop and not to fock
    const config = {
      baseURL: '/',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        [CSRF_FIELD_NAME]: formValues[CSRF_FIELD_NAME]
      }
    }

    this.logger.debug('Sending ProductRating')
    const response = await this.post(
      this.postProductRatingPath(),
      stringify(formValues),
      config
    )
    this.logger.debug('ProductRating send!')
    return response
  }

  async fetchCrossSell(props) {
    const { path, expa, params: passedParams } = props
    this.logger.debug('Fetching CrossSell')

    // TODO: what happens if the cookie isn't set
    const cookie = Cookies.get(ECONDA_VISITOR_ID_COOKIE)

    const params = {
      ...this.baseParams,
      ...passedParams,
      expa,
      emvid: ClientFockService.extractEmvidFromCookie(cookie),
      csc: expa,
      'ctxcustom.content': expa
    }

    // Remove any unexpected URL parameters for the CrossSell widgets
    delete params.mountPoints

    const { status, data } = await this.get(path, { params })

    this.logger.debug('crosssell fetched')

    return {
      status,
      data
    }
  }

  async addProductToWishList(options) {
    const config = {
      ...options,
      baseURL: '/',
      params: {
        ...options.params,
        notepadAction: 'add',
        targetPageCode: PRODUCT_TARGET_PAGE_CODE
      }
    }
    this.logger.debug('adding product to the wishlist')
    const response = await this.get(
      ClientFockService.getUpdateDataPath(),
      config
    )
    this.logger.debug('added product to the wishlist')
    return response
  }

  async deleteProductFromWishList(options) {
    const config = {
      ...options,
      baseURL: '/',
      params: {
        ...options.params,
        notepadAction: 'deleteProduct',
        targetPageCode: PRODUCT_TARGET_PAGE_CODE
      }
    }
    this.logger.debug('removing product from the wishlist')
    const response = await this.get(
      ClientFockService.getUpdateDataPath(),
      config
    )
    this.logger.debug('removed product from the wishlist')
    return response
  }

  async submitProductConsultationMessageForm(passedConfig) {
    const config = { ...passedConfig, baseURL: '/' }
    config.params = { ...config.params, cb: new Date().getTime() }

    this.logger.debug('Submitting ProductConsultationMessageForm')
    const response = await this.get('emailForm.go', config)
    this.logger.debug('ProductConsultationMessageForm submitted')

    return response
  }

  async submitProductConsultationCallbackForm(passedConfig) {
    const config = { ...passedConfig, baseURL: '/' }
    config.params = { ...config.params, cb: new Date().getTime() }

    this.logger.debug('Submitting ProductConsultationMessageForm')
    const response = await this.get('callbackForm.go', config)
    this.logger.debug('ProductConsultationMessageForm submitted')

    return response
  }

  getOptimizelyTrackPath() {
    return `experiments/${this.baseParams.tenantPath}/${this.baseParams.language}/track`
  }

  getOptimizelyActivatePath() {
    return `experiments/${this.baseParams.tenantPath}/${this.baseParams.language}/activate`
  }

  async activateExperiments(featureKeys) {
    this.logger.debug(`Activating features: ${featureKeys}`, 'optimizelyAction')

    const response = await this.post(
      this.getOptimizelyActivatePath(),
      {
        featureKeys
      },
      { withCredentials: true }
    )

    this.logger.debug(`Features activated: ${featureKeys}`, 'optimizelyAction')

    return response
  }

  async trackExperiment(eventKey, eventTags) {
    this.logger.debug(`Tracking event: ${eventKey}`, 'optimizelyAction')

    this.sendBeacon(this.getOptimizelyTrackPath(), {
      eventKey,
      eventTags
    })

    this.logger.debug(
      `Tracking is done for event: ${eventKey}`,
      'optimizelyAction'
    )
  }

  /* eslint-disable-next-line class-methods-use-this */
  getNewsletterPath() {
    return 'newsletter.htm'
  }

  async postNewsletter(formValues) {
    const config = {
      baseURL: '/',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      withCredentials: true
    }

    this.logger.debug('Sending Newsletter')
    const response = await this.post(
      this.getNewsletterPath(),
      stringify(formValues),
      config
    )
    this.logger.debug('Newsletter send!')
    return response
  }

  async fetchProductFromNotepad(upid) {
    const config = {
      headers: {
        'content-type': 'application/json'
      },
      withCredentials: true
    }
    this.logger.debug(`Fetching Notepad Product ${upid}`)
    const r = await this.get(`..${this.getNotepadResourcePath(upid)}`, config)
    this.logger.debug(`Notepad Product ${upid} fetched!`)

    return r
  }

  async addProductToNotepad(upid) {
    const config = {
      headers: {
        'Content-Type': 'application/json'
      },
      withCredentials: true
    }
    this.logger.debug(`Adding Product ${upid} to Notepad`)
    const r = await this.post(
      `..${this.getNotepadResourcePath(upid)}`,
      null,
      config
    )
    this.logger.debug(`Product ${upid} added to Notepad!`)

    return r
  }

  async removeProductFromNotepad(upid) {
    const config = {
      headers: {
        'Content-Type': 'application/json'
      },
      withCredentials: true
    }
    this.logger.debug(`Remove Product ${upid} from Notepad`)
    const r = await this.post(
      `..${this.getNotepadResourceDeletePath(upid)}`,
      null,
      config
    )
    this.logger.debug(`Product ${upid} removed from Notepad!`)

    return r
  }
}

export default ClientFockService
